/** @jsxImportSource ../../i18n */
import React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { cx } from '../../theme/cx';
import { formatMoney, formatPercent, toneOf } from '../../theme/format';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { AnimatedNumber } from './AnimatedNumber';

export const MASKED_AMOUNT = '••••• €';

const TONE_TEXT = { gain: 'text-gain-400', loss: 'text-loss-400', neutral: '' };

export const Money = ({ value, decimals = 2, compact = false, sign = false, colorize = false, sensitive = true, animate = false, currency, className }) => {
  const { hidden } = usePrivacy();
  if (hidden && sensitive) {
    return <span className={cx('num tracking-wider', className)} aria-label="Montant masqué" title="Montant masqué">{MASKED_AMOUNT}</span>;
  }
  const options = { decimals, compact, sign, currency };
  const toneClass = colorize ? TONE_TEXT[toneOf(value)] : '';
  if (animate) {
    return <AnimatedNumber value={value} format={(v) => formatMoney(v, options)} className={cx('num', toneClass, className)} />;
  }
  return <span className={cx('num', toneClass, className)}>{formatMoney(value, options)}</span>;
};

export const Percent = ({ value, decimals = 1, sign = true, colorize = false, className }) => (
  <span className={cx('num', colorize && TONE_TEXT[toneOf(value)], className)}>{formatPercent(value, { decimals, sign })}</span>
);

const DELTA_TONES = {
  gain: ['bg-gain-900/70 text-gain-300 border-gain-500/20', ArrowUpRight],
  loss: ['bg-loss-900/70 text-loss-300 border-loss-500/25', ArrowDownRight],
  neutral: ['bg-surface-3 text-fg-muted border-line-strong', Minus],
};

export const Delta = ({ value, kind = 'percent', decimals, size = 'sm', className }) => {
  const [toneClass, Icon] = DELTA_TONES[toneOf(value)];
  return (
    <span className={cx('inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-semibold', size === 'md' ? 'h-7 px-2.5 text-[13px]' : 'h-6 px-2 text-xs', toneClass, className)}>
      <Icon size={size === 'md' ? 14 : 12} aria-hidden="true" />
      {kind === 'money'
        ? <Money value={value} sign decimals={decimals ?? 0} />
        : <Percent value={value} decimals={decimals ?? 1} />}
    </span>
  );
};
